import { useCallback, useEffect, useMemo } from 'react';
import { usePreSalesPvHandover } from '../use-pre-sales-pv-handover';
import { PreSalesPvDisqualifiedDialog } from './pre-sales-pv-disqualified-dialog';
import { PreSalesPvForwardingResult } from './pre-sales-pv-forwarding-result';
import { PreSalesPvQualifiedDialog } from './pre-sales-pv-qualified-dialog';
import { PreSalesQuestionnaireCard } from './pre-sales-questionnaire-card';
import { PreSalesQuestionnaireForm } from './pre-sales-questionnaire-form';
import { PreSalesQuestionnaireResultContainer } from './pre-sales-questionnaire-result-container';
import {
  PreSalesQuestionnaireProps,
  PreSalesQuestionnaireSnapshot,
} from './pre-sales-questionnaire-types';
import { usePreSalesQuestionnaireForm } from './use-pre-sales-questionnaire-form';
import { usePreSalesSubmission } from './use-pre-sales-submission';

export type {
  PreSalesCancellationEvaluationStatus,
  PreSalesCustomerInfoPatch,
  PreSalesQuestionnaireSnapshot,
} from './pre-sales-questionnaire-types';

export const PreSalesQuestionnaire = ({
  questions,
  unifiedFlow,
  context,
  isAdCase = false,
  footerActions,
  zipCode,
  customer,
  onCustomerInfoChange,
  onStateChange,
  bookZeegSlot,
  hasPendingZeegBooking,
  bookedZeegSlot,
  isLocked = false,
  confirmation,
  onBusyChange,
  onProductCancelled,
  onBooked,
  onCancelled,
}: PreSalesQuestionnaireProps) => {
  const { crmId, hpOrderNumber } = context ?? {};
  const form = usePreSalesQuestionnaireForm({
    questions,
    hpOrderNumber,
    signupId: context?.signupId,
    formId: context?.formId,
    unifiedFlow,
    isLocked,
    onCustomerInfoChange,
  });
  const ordinaryHpCancellationReasons = form.ordinaryHpCancellation.reasons;
  const submission = usePreSalesSubmission({
    questions: form.questions,
    answers: form.answers,
    crmId,
    signupId: context?.signupId,
    formId: context?.formId,
    customerSource: context?.customerSource,
    unifiedFlow,
    isLocked,
    onProductCancelled,
    zipCode,
    prepareForSubmit: form.prepareForSubmit,
    cancellationReasons: form.cancellationReasons,
    hasNoBuildableProduct: form.hasNoBuildableProduct,
    isEvaluatingCancellation: form.isEvaluatingCancellation,
    cancellationEvaluationFailed: form.cancellationEvaluationFailed,
    validateVisibleQuestions: form.validateVisibleQuestions,
    // Submitting a cancelled form is a "reached but not booked" outcome — stop + upload the recording.
    onCancelledSubmit: onCancelled,
  });
  const pvHandover = usePreSalesPvHandover({
    handoverEnabled: !isAdCase && !unifiedFlow,
    visibleQuestions: form.visibleQuestions,
    answers: form.answers,
    cancellationReasons: form.cancellationReasons,
    hasCompletedCancellationEvaluation: form.hasCompletedCancellationEvaluation,
    isEvaluatingCancellation: form.isEvaluatingCancellation,
    cancellationEvaluationFailed: form.cancellationEvaluationFailed,
    context,
  });
  const resetSubmission = submission.reset;
  const resetForwardingResult = pvHandover.resetForwardingResult;
  const prepareForReturnToForm = form.prepareForReturnToForm;

  const handleSubmit = useCallback(
    () => { if (!isLocked) return pvHandover.submitOrForward(submission.submit); },
    [pvHandover.submitOrForward, submission.submit, isLocked],
  );

  const handleBackToForm = useCallback(() => {
    resetSubmission();
    resetForwardingResult();
    prepareForReturnToForm();
  }, [prepareForReturnToForm, resetForwardingResult, resetSubmission]);

  useEffect(() => {
    onBusyChange?.(submission.isSubmitting || pvHandover.isForwarding);
  }, [onBusyChange, submission.isSubmitting, pvHandover.isForwarding]);

  const snapshot = useMemo<PreSalesQuestionnaireSnapshot>(
    () => ({
      qualification: form.qualification,
      answers: form.answers,
      visibleQuestions: form.visibleQuestions,
      cancellationReasons: form.cancellationReasons,
      cancellationEvaluationStatus: form.cancellationEvaluationStatus,
    }),
    [
      form.answers,
      form.qualification,
      form.cancellationEvaluationStatus,
      form.cancellationReasons,
      form.visibleQuestions,
    ],
  );

  useEffect(() => {
    onStateChange?.(snapshot);
  }, [onStateChange, snapshot]);

  useEffect(() => () => {
    onStateChange?.(null);
  }, [onStateChange]);

  if (isLocked && confirmation) {
    return <PreSalesQuestionnaireCard isResult footer={footerActions}>
      {confirmation}
    </PreSalesQuestionnaireCard>;
  }

  if (questions.length === 0) {
    return footerActions ? <PreSalesQuestionnaireCard footer={footerActions}>{null}</PreSalesQuestionnaireCard> : null;
  }

  if (pvHandover.isForwardingResultVisible && pvHandover.forwardedSignUpId) {
    return (
      <PreSalesPvForwardingResult
        signupId={pvHandover.forwardedSignUpId}
        handoverType={pvHandover.handoverType}
        onBooked={onBooked}
        onBackToForm={handleBackToForm}
      />
    );
  }

  if (submission.submissionResult && !isLocked) {
    return (
      <PreSalesQuestionnaireResultContainer
        submissionResult={submission.submissionResult}
        footerActions={footerActions}
        fieldSalesAvailability={submission.fieldSalesAvailability}
        fieldSalesAvailabilityChecked={submission.fieldSalesAvailabilityChecked}
        zeegBookingLinksInfo={submission.zeegBookingLinksInfo}
        bookZeegSlot={bookZeegSlot}
        hasPendingZeegBooking={hasPendingZeegBooking}
        bookedZeegSlot={bookedZeegSlot}
        skipFieldSales={unifiedFlow}
        unifiedFlow={unifiedFlow}
        crmId={crmId}
        signupId={context?.signupId}
        zipCode={zipCode}
        customer={customer}
        onBooked={onBooked}
        onBackToForm={handleBackToForm}
      />
    );
  }

  const hasQualificationCancellation = Boolean(unifiedFlow)
    && form.hasNoBuildableProduct
    && !form.isEvaluatingCancellation
    && !form.cancellationEvaluationFailed
    && ordinaryHpCancellationReasons.length > 0;
  const isCancellationModalOpen = !isLocked && (unifiedFlow
    ? Boolean(submission.pendingCancellation) || (hasQualificationCancellation && !form.isCancellationModalDismissed)
    : ordinaryHpCancellationReasons.length > 0 && !form.isCancellationModalDismissed);
  const isFormLocked = isLocked || (Boolean(unifiedFlow) && isCancellationModalOpen) || submission.isSubmitting || pvHandover.isForwarding;
  const isSubmitDisabled =
    isFormLocked
    || (unifiedFlow ? !context?.signupId : !crmId)
    || (unifiedFlow && !form.hasNoBuildableProduct && !form.areVisibleQuestionsValid)
    || submission.isSaving
    || (!unifiedFlow && (form.isEvaluatingCancellation || form.cancellationEvaluationFailed))
    || submission.isSubmitting
    || pvHandover.isForwarding;

  return (
    <>
      <PreSalesQuestionnaireForm
        unifiedFlow={unifiedFlow}
        answers={form.answers}
        touchedQuestions={form.touchedQuestions}
        visibleQuestions={form.visibleQuestions}
        cancellationQuestions={form.cancellationQuestions}
        cancellationMessages={unifiedFlow && !hasQualificationCancellation
          ? Object.entries(submission.pendingCancellation?.cancellationReasons ?? {})
            .flatMap(([product, reasons]) => reasons.map((reason) => `${product}: ${reason}`))
          : form.ordinaryHpCancellation.messages}
        isCancellationModalOpen={isCancellationModalOpen}
        cancellationEvaluationFailed={form.cancellationEvaluationFailed}
        isFormLocked={isFormLocked}
        isSaving={submission.isSaving}
        isSubmitting={submission.isSubmitting || pvHandover.isForwarding}
        isEvaluatingCancellation={form.isEvaluatingCancellation}
        isSubmitDisabled={isSubmitDisabled}
        canSave={Boolean(crmId)}
        footerActions={footerActions}
        onAnswerChange={form.setAnswer}
        onTouched={form.markTouched}
        onAnswerBlur={form.blurAnswer}
        onSave={submission.save}
        onSubmit={handleSubmit}
        onConfirmCancellation={unifiedFlow ? submission.confirmCancellation : handleSubmit}
        isCancellationConfirmDisabled={isLocked || submission.isSubmitting || (!unifiedFlow && isSubmitDisabled)}
        onDismissCancellationModal={() => {
          submission.dismissCancellation();
          form.dismissCancellationModal();
        }}
      />

      <PreSalesPvQualifiedDialog
        open={!unifiedFlow && pvHandover.isQualifiedDialogOpen}
        handoverType={pvHandover.handoverType}
        isForwarding={pvHandover.isForwarding}
        blockingError={pvHandover.blockingError}
        onConfirm={pvHandover.confirm}
        onDismiss={pvHandover.dismissQualified}
      />

      <PreSalesPvDisqualifiedDialog
        open={!unifiedFlow && pvHandover.isDisqualifiedDialogOpen}
        isPvOnly={pvHandover.isPvOnly}
        isSubmitting={submission.isSubmitting}
        isSubmitDisabled={isSubmitDisabled}
        messages={form.pvCancellation.messages}
        onSubmit={submission.submit}
        onDismiss={pvHandover.dismissDisqualified}
      />
    </>
  );
};
